/**
 * spreadsheetEngine.js — Pure, dependency-free formula engine (T-049).
 *
 * evaluateSheet(cells, rows, cols) -> { values: { A1: computed|errorString, ... } }
 *
 * A cell is a raw string. If it starts with '=' it is a formula; otherwise it is
 * a literal (numeric strings become numbers, everything else stays text).
 *
 * Supported: refs A1 / $A$1 (absolute treated same as relative), ranges A1:B3
 * (inside function args only), numeric and "quoted" string literals, operators
 * + - * / ( ) with precedence, unary minus, comparators = <> < > <= >= (used in
 * IF), and functions SUM, AVERAGE (AVG), MIN, MAX, COUNT, PRODUCT, IF,
 * IFERROR, ISERROR.
 *
 * Errors: #DIV/0!, #REF! (ref out of range / invalid), #CIRC! (circular
 * dependency), #ERR! (parse / type error). Text in arithmetic -> #ERR!; text in
 * SUM/AVERAGE/… is ignored (Excel-like). No eval(): a hand-written tokenizer +
 * recursive-descent parser. Deterministic.
 */

const ERR_DIV0 = '#DIV/0!';
const ERR_REF = '#REF!';
const ERR_CIRC = '#CIRC!';
const ERR_ERR = '#ERR!';

const ERROR_RE = /^#[A-Z0-9/]+!$/;

function isError(v) {
  return typeof v === 'string' && ERROR_RE.test(v);
}

function isRange(v) {
  return v && typeof v === 'object' && v.__range === true;
}

// --- ref helpers -----------------------------------------------------------

/** Column letters -> 1-based index. 'A' -> 1, 'Z' -> 26, 'AA' -> 27. */
function colToNum(letters) {
  let n = 0;
  for (let i = 0; i < letters.length; i++) {
    n = n * 26 + (letters.charCodeAt(i) - 64);
  }
  return n;
}

/** 1-based index -> column letters. 1 -> 'A'. */
function numToCol(n) {
  let s = '';
  while (n > 0) {
    const rem = (n - 1) % 26;
    s = String.fromCharCode(65 + rem) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

/** Parse a ref token (with optional $) -> { col, row, key } or null. */
function parseRef(token) {
  const m = /^\$?([A-Za-z]+)\$?([0-9]+)$/.exec(token);
  if (!m) return null;
  const col = colToNum(m[1].toUpperCase());
  const row = parseInt(m[2], 10);
  return { col, row, key: numToCol(col) + row };
}

/** Try to read a raw cell / literal as a number. Returns null if not numeric. */
function parseNumber(raw) {
  if (typeof raw === 'number') return raw;
  if (typeof raw !== 'string') return null;
  const t = raw.trim();
  if (t === '') return null;
  // Reject things like "1 2" or "1a"; accept 12, -3.5, +2, 1e3, .5
  if (!/^[+-]?(\d+\.?\d*|\.\d+)(e[+-]?\d+)?$/i.test(t)) return null;
  const n = Number(t);
  return Number.isNaN(n) ? null : n;
}

// --- tokenizer -------------------------------------------------------------

function tokenize(input) {
  const tokens = [];
  let i = 0;
  const n = input.length;
  while (i < n) {
    const ch = input[i];
    if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') {
      i++;
      continue;
    }
    if (ch === '"') {
      let j = i + 1;
      let str = '';
      while (j < n && input[j] !== '"') {
        str += input[j];
        j++;
      }
      if (j >= n) throw new Error('parse'); // unterminated string
      tokens.push({ t: 'str', v: str });
      i = j + 1;
      continue;
    }
    if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(input[i + 1] || ''))) {
      let j = i;
      while (j < n && /[0-9.]/.test(input[j])) j++;
      // scientific notation
      if (input[j] === 'e' || input[j] === 'E') {
        j++;
        if (input[j] === '+' || input[j] === '-') j++;
        while (j < n && /[0-9]/.test(input[j])) j++;
      }
      const num = Number(input.slice(i, j));
      if (Number.isNaN(num)) throw new Error('parse');
      tokens.push({ t: 'num', v: num });
      i = j;
      continue;
    }
    if (/[A-Za-z$]/.test(ch)) {
      let j = i;
      while (j < n && /[A-Za-z0-9$]/.test(input[j])) j++;
      tokens.push({ t: 'ident', v: input.slice(i, j) });
      i = j;
      continue;
    }
    if (ch === '<') {
      if (input[i + 1] === '>') { tokens.push({ t: 'op', v: '<>' }); i += 2; continue; }
      if (input[i + 1] === '=') { tokens.push({ t: 'op', v: '<=' }); i += 2; continue; }
      tokens.push({ t: 'op', v: '<' }); i++; continue;
    }
    if (ch === '>') {
      if (input[i + 1] === '=') { tokens.push({ t: 'op', v: '>=' }); i += 2; continue; }
      tokens.push({ t: 'op', v: '>' }); i++; continue;
    }
    if ('=+-*/(),:'.includes(ch)) {
      tokens.push({ t: 'op', v: ch });
      i++;
      continue;
    }
    throw new Error('parse'); // unknown character
  }
  return tokens;
}

// --- parser (recursive descent) -------------------------------------------
//
// Grammar:
//   expr    := comparison
//   comparison := additive ( ('='|'<>'|'<'|'>'|'<='|'>=') additive )?
//   additive   := multiplicative ( ('+'|'-') multiplicative )*
//   multiplicative := unary ( ('*'|'/') unary )*
//   unary   := ('+'|'-')* primary
//   primary := num | str | '(' expr ')' | ref (':' ref)? | FUNC '(' args ')'

function createParser(tokens, ctx) {
  let pos = 0;

  function peek() { return tokens[pos]; }
  function next() { return tokens[pos++]; }
  function expect(v) {
    const tk = tokens[pos];
    if (!tk || tk.v !== v) throw new Error('parse');
    pos++;
  }

  function parseExpr() { return parseComparison(); }

  function parseComparison() {
    let left = parseAdditive();
    const tk = peek();
    if (tk && tk.t === 'op' && ['=', '<>', '<', '>', '<=', '>='].includes(tk.v)) {
      next();
      const right = parseAdditive();
      return compare(left, tk.v, right);
    }
    return left;
  }

  function parseAdditive() {
    let left = parseMultiplicative();
    while (peek() && peek().t === 'op' && (peek().v === '+' || peek().v === '-')) {
      const op = next().v;
      const right = parseMultiplicative();
      left = arith(left, op, right);
    }
    return left;
  }

  function parseMultiplicative() {
    let left = parseUnary();
    while (peek() && peek().t === 'op' && (peek().v === '*' || peek().v === '/')) {
      const op = next().v;
      const right = parseUnary();
      left = arith(left, op, right);
    }
    return left;
  }

  function parseUnary() {
    const tk = peek();
    if (tk && tk.t === 'op' && (tk.v === '+' || tk.v === '-')) {
      next();
      const val = parseUnary();
      if (tk.v === '-') return arith(0, '-', val);
      return arith(0, '+', val);
    }
    return parsePrimary();
  }

  function parsePrimary() {
    const tk = peek();
    if (!tk) throw new Error('parse');

    if (tk.t === 'num') { next(); return tk.v; }
    if (tk.t === 'str') { next(); return tk.v; }

    if (tk.t === 'op' && tk.v === '(') {
      next();
      const val = parseExpr();
      expect(')');
      return val;
    }

    if (tk.t === 'ident') {
      // Function call?  letters only + immediate '('
      const upper = tk.v.toUpperCase();
      const looksFunc = /^[A-Z]+$/.test(upper);
      if (looksFunc && tokens[pos + 1] && tokens[pos + 1].v === '(') {
        next(); // ident
        next(); // '('
        const args = parseArgs();
        expect(')');
        return callFunction(upper, args, ctx);
      }
      // Otherwise a cell ref, maybe a range.
      const ref = parseRef(tk.v);
      if (!ref) throw new Error('parse');
      next();
      if (peek() && peek().t === 'op' && peek().v === ':') {
        next();
        const tk2 = peek();
        if (!tk2 || tk2.t !== 'ident') throw new Error('parse');
        const ref2 = parseRef(tk2.v);
        if (!ref2) throw new Error('parse');
        next();
        return expandRange(ref, ref2, ctx);
      }
      return ctx.getCellValue(ref.key);
    }

    throw new Error('parse');
  }

  function parseArgs() {
    const args = [];
    if (peek() && peek().t === 'op' && peek().v === ')') return args; // no args
    args.push(parseExpr());
    while (peek() && peek().t === 'op' && peek().v === ',') {
      next();
      args.push(parseExpr());
    }
    return args;
  }

  return { parse: () => { const v = parseExpr(); if (pos !== tokens.length) throw new Error('parse'); return v; } };
}

// --- range expansion -------------------------------------------------------

function expandRange(ref1, ref2, ctx) {
  const c1 = Math.min(ref1.col, ref2.col);
  const c2 = Math.max(ref1.col, ref2.col);
  const r1 = Math.min(ref1.row, ref2.row);
  const r2 = Math.max(ref1.row, ref2.row);
  if (c2 > ctx.cols || r2 > ctx.rows || c1 < 1 || r1 < 1) {
    return ERR_REF;
  }
  const out = [];
  for (let r = r1; r <= r2; r++) {
    for (let c = c1; c <= c2; c++) {
      out.push(ctx.getCellValue(numToCol(c) + r));
    }
  }
  return { __range: true, values: out };
}

// --- value coercion + operators -------------------------------------------

function coerceNum(v) {
  if (isError(v)) return v;
  if (typeof v === 'number') return v;
  if (typeof v === 'boolean') return v ? 1 : 0;
  if (v === '' || v === null || v === undefined) return 0; // empty cell -> 0
  const n = parseNumber(v);
  if (n === null) return ERR_ERR; // non-numeric text in arithmetic
  return n;
}

function arith(a, op, b) {
  if (isError(a)) return a;
  if (isError(b)) return b;
  if (isRange(a) || isRange(b)) return ERR_ERR;
  const na = coerceNum(a);
  if (isError(na)) return na;
  const nb = coerceNum(b);
  if (isError(nb)) return nb;
  switch (op) {
    case '+': return na + nb;
    case '-': return na - nb;
    case '*': return na * nb;
    case '/': return nb === 0 ? ERR_DIV0 : na / nb;
    default: return ERR_ERR;
  }
}

function compare(a, op, b) {
  if (isError(a)) return a;
  if (isError(b)) return b;
  if (isRange(a) || isRange(b)) return ERR_ERR;
  let x = a;
  let y = b;
  // Numeric comparison when both look numeric; else string comparison.
  const nx = typeof a === 'number' ? a : parseNumber(a);
  const ny = typeof b === 'number' ? b : parseNumber(b);
  if (nx !== null && ny !== null) { x = nx; y = ny; }
  else { x = a === '' ? '' : String(a); y = b === '' ? '' : String(b); }
  switch (op) {
    case '=': return x === y;
    case '<>': return x !== y;
    case '<': return x < y;
    case '>': return x > y;
    case '<=': return x <= y;
    case '>=': return x >= y;
    default: return ERR_ERR;
  }
}

function toBool(v) {
  if (isError(v)) return v;
  if (typeof v === 'boolean') return v;
  if (typeof v === 'number') return v !== 0;
  if (v === '' || v === null || v === undefined) return false;
  const n = parseNumber(v);
  if (n !== null) return n !== 0;
  const t = String(v).trim().toUpperCase();
  if (t === 'TRUE') return true;
  if (t === 'FALSE') return false;
  return true; // non-empty text is truthy
}

// --- functions -------------------------------------------------------------

/** Flatten scalar + range args into a numbers-only list; ignores text/empty. */
function collectNumbers(args) {
  const nums = [];
  for (const arg of args) {
    if (isError(arg)) throw new EngineError(arg);
    const list = isRange(arg) ? arg.values : [arg];
    for (const v of list) {
      if (isError(v)) throw new EngineError(v);
      if (typeof v === 'number') nums.push(v);
      else if (typeof v === 'string') {
        const nn = parseNumber(v);
        if (nn !== null) nums.push(nn); // numeric text counts; other text ignored
      }
      // booleans / empty ignored (Excel-like)
    }
  }
  return nums;
}

class EngineError {
  constructor(code) { this.code = code; }
}

function callFunction(name, args, ctx) {
  try {
    switch (name) {
      case 'SUM':
        return collectNumbers(args).reduce((a, b) => a + b, 0);
      case 'AVERAGE':
      case 'AVG': {
        const ns = collectNumbers(args);
        if (ns.length === 0) return ERR_DIV0;
        return ns.reduce((a, b) => a + b, 0) / ns.length;
      }
      case 'MIN': {
        const ns = collectNumbers(args);
        return ns.length ? Math.min(...ns) : 0;
      }
      case 'MAX': {
        const ns = collectNumbers(args);
        return ns.length ? Math.max(...ns) : 0;
      }
      case 'COUNT':
        return collectNumbers(args).length;
      case 'PRODUCT': {
        const ns = collectNumbers(args);
        return ns.length ? ns.reduce((a, b) => a * b, 1) : 0;
      }
      case 'IF': {
        if (args.length < 2) return ERR_ERR;
        const cond = args[0];
        if (isError(cond)) return cond;
        if (isRange(cond)) return ERR_ERR;
        const b = toBool(cond);
        if (isError(b)) return b;
        return b ? args[1] : (args.length >= 3 ? args[2] : false);
      }
      case 'IFERROR': {
        // IFERROR(value, fallback): fallback replaces value only when value is
        // an error code (#REF!, #DIV/0!, …); otherwise value passes through.
        if (args.length < 2) return ERR_ERR;
        return isError(args[0]) ? args[1] : args[0];
      }
      case 'ISERROR':
        // ISERROR(value) -> TRUE when value is an error code, else FALSE.
        return args.length >= 1 && isError(args[0]);
      default:
        return ERR_ERR; // unknown function
    }
  } catch (e) {
    if (e instanceof EngineError) return e.code;
    return ERR_ERR;
  }
}

// --- public API ------------------------------------------------------------

/**
 * Evaluate every non-empty cell of a sheet.
 * @param {Object<string,string>} cells - map of ref -> raw string.
 * @param {number} rows - number of rows (bounds for #REF!).
 * @param {number} cols - number of columns (bounds for #REF!).
 * @returns {{ values: Object<string, (number|string|boolean)> }}
 */
export function evaluateSheet(cells, rows, cols) {
  const src = cells || {};
  const R = rows || 20;
  const C = cols || 8;
  const cache = {};
  const visiting = new Set();

  const ctx = {
    rows: R,
    cols: C,
    getCellValue,
  };

  function getCellValue(key) {
    const ref = parseRef(key);
    if (!ref) return ERR_REF;
    if (ref.col > C || ref.row > R || ref.col < 1 || ref.row < 1) return ERR_REF;
    const nk = ref.key;

    if (Object.prototype.hasOwnProperty.call(cache, nk)) return cache[nk];
    if (visiting.has(nk)) return ERR_CIRC;

    const raw = src[nk] !== undefined ? src[nk] : src[key];
    if (raw === undefined || raw === null || raw === '') {
      cache[nk] = '';
      return '';
    }

    if (typeof raw === 'string' && raw[0] === '=') {
      visiting.add(nk);
      let val;
      try {
        const tokens = tokenize(raw.slice(1));
        val = createParser(tokens, ctx).parse();
        if (isRange(val)) val = ERR_ERR; // a bare range is not a scalar result
      } catch (e) {
        val = ERR_ERR;
      }
      visiting.delete(nk);
      cache[nk] = val;
      return val;
    }

    // Literal: numeric string -> number, otherwise text.
    const num = parseNumber(raw);
    const val = num !== null ? num : String(raw);
    cache[nk] = val;
    return val;
  }

  const values = {};
  for (const key of Object.keys(src)) {
    const ref = parseRef(key);
    const nk = ref ? ref.key : key;
    values[nk] = getCellValue(key);
  }
  return { values };
}

/** Format a computed value for display in a grid cell. */
export function formatValue(v) {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return v ? 'VERDADERO' : 'FALSO';
  if (typeof v === 'number') {
    if (!Number.isFinite(v)) return ERR_ERR;
    // Trim floating noise without forcing decimals on integers. Use toLocaleString
    // (NOT String()) so very small / very large numbers render in plain decimal —
    // String(1e-7)==='1e-7' but the Python engine emits '0.0000001'; the grid and
    // the AI cell tools (read_cells/set_cell) must agree on the same text.
    const r = Math.round(v * 1e10) / 1e10;
    return r.toLocaleString('en-US', { useGrouping: false, maximumFractionDigits: 10 });
  }
  return String(v);
}

export const _internals = { tokenize, parseRef, colToNum, numToCol, parseNumber };
